import StraightenIcon from '@mui/icons-material/Straighten';
import Button from 'common/components/Elements/Button/Button';
import ButtonSeparator from 'common/components/Elements/ButtonSeparator/ButtonSeparator';
import { DownloadIcon } from 'common/components/Elements/Icon/Icon';
import type { ToggleButtonOption } from 'common/components/Elements/ToggleButton/ToggleButton';
import ToggleButton from 'common/components/Elements/ToggleButton/ToggleButton';
import { downloadCanvasFile } from 'common/utilities/browser/downloadFile/downloadFile';
import { generateRandomAZString } from 'common/utilities/core/string';
import I18n from 'i18n/en-US';
import React, {
  useRef, useState,
} from 'react';
import type { SequenceAlign } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import type { HandleAlignedSequencesChange } from 'sequenceAnalyser/components/SequenceAnalyser/SequenceAnalyser';
import useSortBySimilarity from 'sequenceAnalyser/utilities/similarityTree/useSortBySimilarity';

export type ShowDistanceToggle = 'on' | 'off';

const showDistanceToggleOptions = (): ToggleButtonOption<ShowDistanceToggle>[] => ([
  {
    id: 'on',
    title: I18n.SequenceAnalyser.SimilarityTree.showDistanceToggleOptions.onTitle,
    content: (
      <StraightenIcon fontSize="small" />
    ),
    active: true,
  },
  {
    id: 'off',
    title: I18n.SequenceAnalyser.SimilarityTree.showDistanceToggleOptions.offTitle,
    content: (
      <StraightenIcon fontSize="small" />
    ),
  },
]);

function useSimilarityTreeMenu(
  alignedSequences: SequenceAlign[],
  onAlignedSequencesChange: HandleAlignedSequencesChange,
) {
  const [showDistanceToggle, setShowDistanceToggle] = useState<ShowDistanceToggle>('off');

  const { sortBySimilarity, SortBySimilarityLoading } = useSortBySimilarity();

  const canvasIdRef = useRef(generateRandomAZString(10));

  const SimilarityTreeMenu = (
    <>
      <Button
        action="secondary"
        onClick={() => sortBySimilarity(alignedSequences, onAlignedSequencesChange)}
        small
        mr
        title={I18n.SequenceAnalyser.SimilarityTree.sortTitle}
      >
        {I18n.SequenceAnalyser.SimilarityTree.sort}
      </Button>

      <ToggleButton
        variant="single"
        onChange={(option) => {
          setShowDistanceToggle(option.id === 'on' ? 'off' : 'on');
        }}
        options={showDistanceToggleOptions()}
        selectedOption={
          showDistanceToggleOptions().find((o) => (
            o.id === showDistanceToggle
          )) as ToggleButtonOption<ShowDistanceToggle>
        }
      />

      <ButtonSeparator />

      <Button
        action="secondary"
        title={I18n.SequenceAnalyser.SimilarityTree.download}
        onClick={() => {
          const el = document.querySelector(`#${canvasIdRef.current}`) as HTMLCanvasElement | undefined;
          if (el) downloadCanvasFile(el, 'SimilarityTree');
        }}
        buttonIcon
      >
        <DownloadIcon />
      </Button>
    </>
  );

  return {
    SimilarityTreeMenu,
    SortBySimilarityLoading: SortBySimilarityLoading || undefined,
    canvasId: canvasIdRef.current,
    showDistanceToggle,
  };
}

export default useSimilarityTreeMenu;
